import { useReducer } from 'react';

import { initialSentenceState } from '../helpers/dummyElement';
import {
    addSentenceDimension,
    addSentenceFilter,
    addSentenceMetric,
    changeSentenceDimension,
    changeSentenceFilter,
    changeSentenceFiltersConnector,
    changeSentenceMetric,
    removeQueryItem,
    removeSentenceFilter,
    resetSentence,
    setSentenceQuery,
    splitSentenceFilterGroup,
    transformSentenceFilterToGroup,
} from '../helpers/reducer';

export enum SentenceActionType {
    addMetric,
    changeMetric,
    addDimension,
    changeDimension,
    addFilter,
    changeFilter,
    changeFiltersConnector,
    removeFilter,
    transformToGroup,
    splitGroup,
    reset,
    removeItem,
    setSentence,
}

type SentencePayloadlessActions = SentenceActionType.reset;

export type SentenceAction =
    | {
          type: SentenceActionType.addMetric;
          payload?: { field: string; aggregation: Aggregation };
      }
    | { type: Extract<SentenceActionType, SentencePayloadlessActions> }
    | { type: SentenceActionType.changeMetric; metric: SentenceMetricT }
    | {
          type: SentenceActionType.changeDimension;
          dimension: SentenceDimensionT;
      }
    | {
          type: SentenceActionType.changeFilter;
          path: number[];
          filter: SentenceFilterT;
      }
    | {
          type: SentenceActionType.changeFiltersConnector;
          path: number[];
          connector: FilterConnectorT;
      }
    | { type: SentenceActionType.addDimension; dimension?: AvailableDimensionT }
    | {
          type: SentenceActionType.addFilter;
          path?: number[];
          filter?: AvailableElementT;
      }
    | { type: SentenceActionType.removeFilter; path: number[] }
    | { type: SentenceActionType.transformToGroup; path: number[] }
    | { type: SentenceActionType.splitGroup; path: number[] }
    | { type: SentenceActionType.removeItem; index: number }
    | { type: SentenceActionType.setSentence; query: SentenceQueryT };

const sentenceReducer = (
    state: SentenceQueryT,
    action: SentenceAction,
): SentenceQueryT => {
    switch (action.type) {
        case SentenceActionType.addMetric:
            return addSentenceMetric(state, action.payload);
        case SentenceActionType.addDimension:
            return addSentenceDimension(state, action.dimension);
        case SentenceActionType.addFilter:
            return addSentenceFilter(state, action.path, action.filter);
        case SentenceActionType.changeMetric:
            return changeSentenceMetric(state, action.metric);
        case SentenceActionType.changeDimension:
            return changeSentenceDimension(state, action.dimension);
        case SentenceActionType.changeFilter:
            return changeSentenceFilter(state, action.path, action.filter);
        case SentenceActionType.changeFiltersConnector:
            return changeSentenceFiltersConnector(
                state,
                action.path,
                action.connector,
            );
        case SentenceActionType.removeFilter:
            return removeSentenceFilter(state, action.path);
        case SentenceActionType.transformToGroup:
            return transformSentenceFilterToGroup(state, action.path);
        case SentenceActionType.splitGroup:
            return splitSentenceFilterGroup(state, action.path);
        case SentenceActionType.reset:
            return resetSentence();
        case SentenceActionType.removeItem:
            return removeQueryItem(state, action.index);
        case SentenceActionType.setSentence:
            return setSentenceQuery(state, action.query);
        default:
            return state;
    }
};

export const useSentenceReducer = () =>
    useReducer(sentenceReducer, initialSentenceState);
